import type { FilingOut, FilingPreviewOut } from '@/api/types';
import { formatCurrency } from '@/lib/format';

/** Derived state for the Create-filing control. Pure so vitest covers the
 *  pending / error / count-0 / singular / plural branches without a browser
 *  drive. */
export interface CreateFilingAffordance {
  disabled: boolean;
  /** Button-adjacent summary, e.g. "File 3 approved findings · $450.00 CAD". */
  summary: string;
}

/** Mirrors TanStack Query's `status` union so the component passes it
 *  straight through. */
export type PreviewStatus = 'pending' | 'error' | 'success';

export function createFilingAffordance(
  preview: FilingPreviewOut | undefined,
  status: PreviewStatus,
): CreateFilingAffordance {
  // A missing preview is not the same as an empty delta — say what's
  // actually happening instead of claiming there's nothing to file.
  if (status === 'pending') {
    return { disabled: true, summary: 'Checking for newly-approved findings…' };
  }
  if (status === 'error' || !preview) {
    return { disabled: true, summary: "Couldn't load the filing preview — refresh to retry" };
  }
  if (preview.count === 0) {
    return { disabled: true, summary: 'No newly-approved findings to file' };
  }
  const noun = preview.count === 1 ? 'finding' : 'findings';
  return {
    disabled: false,
    summary: `File ${preview.count} approved ${noun} · ${formatCurrency(preview.recovery_total)}`,
  };
}

/** Filings arrive oldest-first from the API; the table shows newest first
 *  so the most recent filing (the only deletable one, come un-file) leads. */
export function newestFirst(filings: readonly FilingOut[]): FilingOut[] {
  return [...filings].sort((a, b) => b.sequence_number - a.sequence_number);
}
